import { useState } from "react";
import {
  Star,
  BookOpen,
  Trash2,
  Zap,
  CheckCircle2,
  HelpCircle,
  Info,
  Check,
  X,
} from "lucide-react";

/* German demo text from the actual app (Rotkäppchen) — each token tagged
   with an initial state. User can click to cycle. */
const initialTokens = [
  { t: "Es", s: "new" },
  { t: "war", s: "new" },
  { t: "einmal", s: "new" },
  { t: "eine", s: "new" },
  { t: "kleine", s: "new" },
  { t: "süße", s: "new" },
  { t: "Dirne,", s: "new" },
  { t: "die", s: "new" },
  { t: "hatte", s: "new" },
  { t: "jedermann", s: "new" },
  { t: "lieb,", s: "new" },
  { t: "der", s: "new" },
  { t: "sie", s: "new" },
  { t: "nur", s: "new" },
  { t: "ansah,", s: "new" },
  { t: "am", s: "new" },
  { t: "allerliebsten", s: "new" },
  { t: "aber", s: "new" },
  { t: "ihre", s: "new" },
  { t: "Großmutter.", s: "new" },
  { t: "—br—" },
  { t: "Die", s: "new" },
  { t: "wusste", s: "new" },
  { t: "gar", s: "new" },
  { t: "nicht,", s: "new" },
  { t: "was", s: "new" },
  { t: "sie", s: "new" },
  { t: "alles", s: "new" },
  { t: "dem", s: "new" },
  { t: "Kinde", s: "new" },
  { t: "geben", s: "new" },
  { t: "sollte.", s: "new" },
  { t: "—br—" },
  { t: "Einmal", s: "new" },
  { t: "schenkte", s: "new" },
  { t: "sie", s: "new" },
  { t: "ihm", s: "new" },
  { t: "ein", s: "new" },
  { t: "Käppchen", s: "new" },
  { t: "von", s: "new" },
  { t: "rotem", s: "new" },
  { t: "Samt.", s: "new" },
];

const cycle = { new: "known", known: "priority", priority: "ignored", ignored: "new" };

const priorityWords = [
  { w: "es"},
  { w: "sie"},
  { w: "ein" },
];

const newWordsList = [
  "Dirne",
  "jedermann",
  "ansah",
  "Großmutter",
  "wusste",
  "Kinde",
  "schenkte",
  "Käppchen",
  "rotem"
];

const learnedRecent = [];

const legend = [
  { c: "var(--vb-known)", lbl: "Already known", glyph: "✓" },
  { c: "var(--vb-priority)", lbl: "Priority (top 3 new)", glyph: "★" },
  { c: "var(--vb-new)", lbl: "Not familiar yet", glyph: "●" },
  { c: "var(--vb-muted)", lbl: "Ignored for 14 days", glyph: "—" },
];

export const ReaderDemo = () => {
  const [tokens, setTokens] = useState(initialTokens);
  const [legendOpen, setLegendOpen] = useState(false);

  const onClick = (i) => {
    setTokens((prev) =>
      prev.map((tok, idx) =>
        idx === i && tok.s ? { ...tok, s: cycle[tok.s] } : tok
      )
    );
  };

  const totals = tokens.reduce(
    (acc, t) => {
      if (!t.s) return acc;
      acc[t.s] = (acc[t.s] || 0) + 1;
      return acc;
    },
    { known: 0, new: 0, priority: 0, ignored: 0 }
  );

  // Words marked as known on this page (from clicking tokens)
  const knownOnPage = tokens
    .filter((t) => t.s === "known")
    .map((t) => t.t.replace(/[.,:]/g, ""));
  // Combine static learned words with user-marked known words
  const allLearned = [...new Set([...learnedRecent, ...knownOnPage])];

  return (
    <section id="demo" data-testid="demo-section" className="relative py-24 sm:py-28 border-t border-[var(--vb-border)]">
      <div className="container-x">
        <div className="mb-10 reveal">
          <div className="mono-label mb-3">[ 03 · Try the reader ]</div>
          <h2 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.02]">
            Click any word.{" "}
            <span className="text-[var(--vb-muted)] italic font-light">
              This is the actual reader*.
            </span>
          </h2>
          <p className="text-sm italic text-[var(--vb-muted)] leading-relaxed mt-3 max-w-2xl">
            *Well, mostly. Verbatim's actual reader has a dictionary included,
            more options, more flexibility and prettier visuals.
          </p>
          <p className="mt-4 max-w-2xl text-[var(--vb-muted)] text-base">
            Below is a live mock of the Verbatim reader running on a German
            fairy tale. Click a word to cycle through states —{" "}
            <span className="rw rw-new !cursor-default">new</span> →{" "}
            <span className="rw rw-known !cursor-default">known</span> →{" "}
            <span className="rw rw-priority !cursor-default">priority</span> →{" "}
            <span className="rw rw-ignored !cursor-default">ignored</span>.
            Watch the panels update.
          </p>
        </div>

        <div className="card-soft p-4 sm:p-5 reveal" data-testid="reader-card">
          {/* App header bar */}
          <div className="flex items-center justify-between flex-wrap gap-3 px-2 pb-4 border-b border-[var(--vb-border)] mb-5">
            <button data-testid="reader-back" className="text-sm text-[var(--vb-muted)] hover:text-white flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[var(--vb-surface-2)] border border-[var(--vb-border)]">
              ← Back to Deutsch Dashboard
            </button>
            <div className="flex-1 text-center min-w-0">
              <div className="font-display font-extrabold text-xl sm:text-2xl truncate">
                Rotkäppchen - Brüder Grimm <span className="text-[var(--vb-muted)] font-normal text-base italic">Chapter 1</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button data-testid="reader-fav" className="pill pill-priority"><Star size={11} fill="currentColor" /> Favorite</button>
              <button data-testid="reader-reading" className="pill pill-blue"><BookOpen size={11} /> Reading</button>
              <button data-testid="reader-delete" className="pill" style={{ color: "var(--vb-new)", borderColor: "var(--vb-new-border)", background: "var(--vb-new-bg)" }}><Trash2 size={11} /></button>
            </div>
          </div>

          {/* 3-column reader layout */}
          <div className="grid lg:grid-cols-12 gap-5">
            {/* LEFT: Learned on this page */}
            <aside className="lg:col-span-3 space-y-5">
              <div className="card-soft p-4" data-testid="panel-learned">
                <div className="flex items-center gap-2 mb-3">
                  <Zap size={14} className="text-[var(--vb-priority)]" />
                  <span className="font-display font-bold text-sm">
                    Learned on this page
                  </span>
                </div>
                <div className="text-[10px] text-[var(--vb-muted)] mb-3 leading-snug">Words you've marked as known, most recent first.</div>
                {allLearned.length === 0 ? (
                  <div className="border border-dashed border-[var(--vb-border-strong)] rounded-lg py-6 text-center text-xs text-[var(--vb-muted)] italic">No recent changes</div>
                ) : (
                  <div className="flex flex-wrap gap-1.5 max-h-64 overflow-hidden">
                    {allLearned.map((w, i) => (
                      <span key={i} className="rw rw-known text-xs">{w} <X size={9} className="inline opacity-60" /></span>
                    ))}
                  </div>
                )}
              </div>

              <div className="card-soft p-4" data-testid="panel-known">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle2 size={14} className="text-[var(--vb-known)]" />
                  <span className="font-display font-bold text-sm">Words you know</span>
                </div>
                <div className="text-[10px] text-[var(--vb-muted)] mb-3 leading-snug">Words already in your vocabulary, most recent first.</div>
                <div className="flex flex-wrap gap-1.5 max-h-48 overflow-hidden">
                  {learnedRecent.slice(0, 14).map((w, i) => (
                    <span key={i} className="rw rw-known text-xs">{w} <X size={9} className="inline opacity-60" /></span>
                  ))}
                </div>
              </div>
            </aside>

            {/* MIDDLE: reader */}
            <div className="lg:col-span-6">
              <div
                className="card-soft p-6 sm:p-7 leading-loose text-[17px] sm:text-[18px]"
                data-testid="reader-paragraph"
                style={{ fontFamily: "'Cabinet Grotesk', serif" }}
              >
                <div className="flex items-center gap-2 mb-5 pb-4 border-b border-[var(--vb-border)]">
                  <BookOpen size={14} className="text-[var(--vb-primary)]" />
                  <span className="font-display font-bold">
                    Rotkäppchen
                  </span>
                  <span className="text-xs text-[var(--vb-muted)] italic">
                    (Page 1 of 11)
                  </span>
                </div>
                {tokens.map((tok, i) =>
                  tok.t === "—br—" ? (
                    <div key={i} className="h-3" />
                  ) : (
                    <button key={i} data-testid={`token-${i}`} onClick={() => onClick(i)} className={`rw rw-${tok.s ?? "plain"}`}>
                      {tok.t}
                    </button>
                  )
                )}
              </div>

              <div className="flex items-center justify-between mt-4 text-xs text-[var(--vb-muted)]">
                <button data-testid="reader-prev-page" className="hover:text-white">← Previous page</button>
                <span className="font-mono">
                  page 1 / 8 · <span className="text-[var(--vb-known)]">{totals.known}</span> known · <span className="text-[var(--vb-new)]">{totals.new}</span> new · <span className="text-[var(--vb-priority)]">{totals.priority}</span> priority
                </span>
                <button data-testid="reader-next-page" className="hover:text-white">Next page →</button>
              </div>
            </div>

            {/* RIGHT: Priority + New words */}
            <aside className="lg:col-span-3 space-y-5">
              <div className="card-soft p-4" data-testid="panel-priority">
                <div className="flex items-center gap-2 mb-2">
                  <HelpCircle size={14} className="text-[var(--vb-new)]" />
                  <span className="font-display font-bold text-sm">Priority</span>
                </div>
                <p className="text-[10px] text-[var(--vb-muted)] mb-3 leading-snug">The 3 most common new words — learn these first.</p>
                <div className="space-y-2">
                  {priorityWords.map((p, i) => (
                    <div key={i} data-testid={`priority-${i}`} className="flex items-center justify-between gap-2 rw rw-new !cursor-default !rounded-lg !px-3 !py-2">
                      <span className="font-display font-bold">{p.w}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="card-soft p-4" data-testid="panel-new">
                <div className="flex items-center gap-2 mb-2">
                  <HelpCircle size={14} className="text-[var(--vb-muted)]" />
                  <span className="font-display font-bold text-sm">New words</span>
                </div>
                <p className="text-[10px] text-[var(--vb-muted)] mb-3 leading-snug">Less common new words.</p>
                <div className="grid grid-cols-2 gap-1.5">
                  {newWordsList.map((w, i) => (
                    <button key={i} data-testid={`newword-${i}`} className="rw rw-new text-xs !flex !items-center !justify-between !w-full">
                      <span>{w}</span>
                      <Check size={10} className="opacity-70 ml-1 text-[var(--vb-known)]" />
                    </button>
                  ))}
                </div>
              </div>
            </aside>
          </div>

          {/* Word legend popover */}
          <div className="relative mt-5">
            <button data-testid="legend-toggle" onClick={() => setLegendOpen((v) => !v)} className="pill text-xs">
              <Info size={11} /> Word legend
              <span className="text-[var(--vb-muted)] ml-1">{legendOpen ? "▾" : "▸"}</span>
            </button>
            {legendOpen && (
              <div data-testid="legend-popover" className="card-soft p-4 mt-2 inline-flex flex-wrap gap-x-5 gap-y-2">
                {legend.map((l, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs">
                    <span className="w-2 h-2 rounded-full" style={{ background: l.c }} />
                    <span className="text-[var(--vb-muted)]"><span className="text-white">{l.lbl}</span></span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};